/**
 * Statistical Report Page (統計一覧表 — /rezepts/statistical_init).
 *
 * Legacy RA2 ⇢ RezeptController@initStatistical / @statisticalSearch
 *           R2 ⇢ RRezeptController@initStatistical / @statisticalSearch
 *
 * A2/R2 split resolved via useTheme().serviceType.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { AppButton, AppModal, AppSpin } from '@/components/atoms';
import BottomBar from '@/components/organisms/Footer';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { convGtJMonth, warekiMonthToYearmonth } from '@/utils/dateConverter';
import { useStatisticalMassagersQuery } from '@/hooks/main/useStatisticalMassagersQuery';
import { useStatisticalSearchQuery } from '@/hooks/main/useStatisticalSearchQuery';
import { useStatisticalPrintOtherMutation } from '@/hooks/main/useStatisticalPrintOtherMutation';
import { useStatisticalPrintListMutation } from '@/hooks/main/useStatisticalPrintListMutation';
import { useStatisticalPrintBillingMutation } from '@/hooks/main/useStatisticalPrintBillingMutation';
import StatisticalReportSearchForm from './components/StatisticalReportSearchForm';
import StatisticalReportTable from './components/StatisticalReportTable';
import StatisticalReportPdfMenuModal from './components/StatisticalReportPdfMenuModal';
import type { StatisticalSearchFormValues } from './types';

const StatisticalReportPage: React.FC = () => {
  const { serviceType, colors } = useTheme();
  const sidebarOpen = useSidebarOpen();

  const defaultYearmonth = useMemo(
    () => String(warekiMonthToYearmonth(convGtJMonth(dayjs())) ?? ''),
    []
  );
  const [currentYearmonth, setCurrentYearmonth] = useState<string>(defaultYearmonth);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);

  // Track last searched params for PDF buttons
  const lastSearchRef = useRef<StatisticalSearchFormValues | null>(null);

  const { massagers } = useStatisticalMassagersQuery(currentYearmonth || null);
  const { rows, totals, hasData, hasSearched, isLoading, triggerSearch } =
    useStatisticalSearchQuery();
  const { triggerPrint: triggerOtherPrint, isLoading: isPrintingOther } =
    useStatisticalPrintOtherMutation();
  const { triggerPrint: triggerListPrint, isLoading: isPrintingList } =
    useStatisticalPrintListMutation();
  const { triggerPrint: triggerBillingPrint, isLoading: isPrintingBilling } =
    useStatisticalPrintBillingMutation();

  const isPrinting = isPrintingOther || isPrintingList || isPrintingBilling;
  const isPrintDisabled = isLoading || !hasData || isPrinting;
  const disabledPrintButtonStyle: React.CSSProperties = {
    backgroundColor: colors.secondary,
    borderColor: colors.secondary,
    color: '#fff',
    cursor: 'default',
    opacity: 'var(--ant-opacity-loading)',
  };

  const handleYearmonthChange = useCallback((yyyymm: string) => {
    setCurrentYearmonth(yyyymm);
  }, []);

  const handleSearch = useCallback(
    (values: StatisticalSearchFormValues) => {
      lastSearchRef.current = values;
      triggerSearch(values);
    },
    [triggerSearch]
  );

  const handlePrintOther = useCallback(
    (pdfPattern: '1' | '2' | '3' | '4') => {
      const p = lastSearchRef.current;
      if (!p) return;
      triggerOtherPrint(p.searchYearmonth, p.massagerId, pdfPattern);
    },
    [triggerOtherPrint]
  );

  const handlePrintList = useCallback(
    async (payload: {
      yearmonth: string;
      massagerIds: (number | 'all')[];
      outputOption: boolean;
      outputOption2: boolean;
    }) => {
      await triggerListPrint(payload);
    },
    [triggerListPrint]
  );

  const handlePrintBilling = useCallback(() => {
    const p = lastSearchRef.current;
    if (!p) return;
    triggerBillingPrint(p.searchYearmonth);
  }, [triggerBillingPrint]);

  return (
    <div className="pb-4">
      <StatisticalReportSearchForm
        onSearch={handleSearch}
        onYearmonthChange={handleYearmonthChange}
        massagers={massagers}
        serviceType={serviceType ?? ''}
        isLoading={isLoading}
      />

      {hasSearched && (
        <CollapsibleCard>
          <StatisticalReportTable rows={rows} totals={totals} />
        </CollapsibleCard>
      )}

      <BottomBar
        variant="action"
        visible={hasSearched}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <div className="flex gap-2">
            {(
              [
                { pattern: '1', label: '保険者別明細(本レセ)' },
                { pattern: '2', label: '保険者別明細(後期高齢)' },
                { pattern: '3', label: '保険者別明細(医療助成)' },
                { pattern: '4', label: '生活保護分集計' },
              ] as const
            ).map(({ pattern, label }) => (
              <AppButton
                key={pattern}
                type="primary"
                disabled={isPrintDisabled}
                className="font-medium text-white rounded"
                style={isPrintDisabled ? disabledPrintButtonStyle : undefined}
                loading={isPrintingOther}
                onClick={() => handlePrintOther(pattern)}
              >
                {label}
              </AppButton>
            ))}
            <AppButton
              type="primary"
              disabled={isPrintDisabled}
              className="font-medium text-white rounded"
              style={isPrintDisabled ? disabledPrintButtonStyle : undefined}
              loading={isPrintingList}
              onClick={() => setIsPdfMenuOpen(true)}
            >
              保険請求統計一覧
            </AppButton>
            {/* 請求一覧 (HIT-4471): corrected per-insurer 本証/医療助成 billing report */}
            <AppButton
              type="primary"
              disabled={isPrintDisabled}
              className="font-medium text-white rounded"
              style={isPrintDisabled ? disabledPrintButtonStyle : undefined}
              loading={isPrintingBilling}
              onClick={handlePrintBilling}
            >
              請求一覧
            </AppButton>
          </div>
        }
      />

      <StatisticalReportPdfMenuModal
        isOpen={isPdfMenuOpen}
        onClose={() => setIsPdfMenuOpen(false)}
        yearmonth={lastSearchRef.current?.searchYearmonth ?? currentYearmonth}
        massagers={massagers}
        serviceType={serviceType ?? ''}
        onPrint={handlePrintList}
        isPrinting={isPrintingList}
      />

      {/* HIT-14704: 検索 STEP 2 — validation passed → loading modal 「処理中」 (spinner + text)
          until the search response arrives (legacy statistical.blade.php #loadingModal). */}
      <AppModal
        isOpen={isLoading}
        onClose={() => {}}
        footer={null}
        closable={false}
        maxWidth={200}
      >
        <div className="flex flex-col items-center gap-3 py-4" data-testid="statistical-search-loading">
          <AppSpin />
          <span className="text-sm">処理中</span>
        </div>
      </AppModal>
    </div>
  );
};

export default StatisticalReportPage;
